import React, {createContext, useCallback, useContext, useMemo} from 'react';
import {fetchGET, fetchGETWithExtraHeaders, fetchPOST} from "../api/fetch";
import {useQuery, useQueryClient} from "@tanstack/react-query";

const AuthenticationContext = createContext();

const REACT_QUERY_ID_USERNAME = "username";

const AUTH_URL = "/api/authenticate";
const SIGNUP_URL = "/api/signup";
const LOGOUT_URL = "/logout";

const authQueryConfig = {
    staleTime: 0,
    cacheTime: 1000 * 60 * 5,
    retry: false
}

export function useAuthenticated() {
    const result = useQuery({
        queryKey: [REACT_QUERY_ID_USERNAME],
        queryFn: async () => {
            try {
                return await fetchGET(AUTH_URL) || ""
            } catch (e) {
                return ""; //means not logged in
            }
        },
        ...authQueryConfig
    });
    return {
        ...result,
        memberNumber: result?.data?.memberNumber,
        username: result.data?.username,
        role: result.data?.role,
        sectorControl: result.data?.sector
    };
}

export function AuthenticationProvider(props) {
    const {username, role, memberNumber, sectorControl} = useAuthenticated();
    const queryClient = useQueryClient()

    const authenticate = useCallback(async (username, password) => {
        const extraHeaders = {authorization: "Basic " + window.btoa(`${username}:${password}`)};
        try {
            return await fetchGETWithExtraHeaders(AUTH_URL, extraHeaders);
        } catch (e) {

        } finally {
            queryClient.invalidateQueries([REACT_QUERY_ID_USERNAME])
        }
    }, [, queryClient]);

    const signup = useCallback(async (username, sector, memberNumber, email, password) => {
        try {
            return await fetchPOST(SIGNUP_URL, {username, sector, memberNumber, email, password});
        } catch (e) {

        } finally {
            queryClient.invalidateQueries([REACT_QUERY_ID_USERNAME])
        }
    }, [queryClient]);

    const logout = useCallback(async () => {
        try {
            return await fetchPOST(LOGOUT_URL);
        } finally {
            await queryClient.invalidateQueries([REACT_QUERY_ID_USERNAME])
        }
    }, [queryClient]);

    const isLoggedIn = useMemo(() => !!username, [username]);

    const api = useMemo(() => ({
            username,
            memberNumber,
            role,
            sectorControl,
            authenticate,
            signup,
            logout,
            isLoggedIn
        }), [username, memberNumber, role, sectorControl, authenticate, signup, logout, isLoggedIn]
    );

    return (
        <AuthenticationContext.Provider value={api}>
            {props.children}
        </AuthenticationContext.Provider>
    )
}

export const useAuthenticationContext = () => useContext(AuthenticationContext);
